import React, { useContext, useMemo } from 'react'
import {
    Grid2 as Grid,
    Card,
    CardHeader,
    CardContent,
    List,
    ListItem,
    IconButton,
    Typography,
} from '@mui/material'
import { LoadingContext, MButton } from '../../M_Components'
import PropTypes from 'prop-types'
import { t } from 'i18next'
import './MTransferList.css'

export function MTransferList({
    header,
    value,
    setValue,
    primaryColor,
    secondaryColor,
    entity,
    field,
    valueIdField,
    disabled,
    height = 500,
    width = '100%',
    maxWidth = 500,
    borderRadius = 8,
}) {
    const ctx = useContext(LoadingContext)

    // Resolve dynamic colors from props or global theme context
    const resolvedPrimary = primaryColor || ctx.primaryColor || 'var(--color-black)'
    const resolvedSecondary = secondaryColor || ctx.secondaryColor || 'var(--color-black)'

    const items = Array.isArray(value) ? value : []

    const handleDelete = (obj) => {
        if (disabled) return
        setValue(items.filter((item) => item?.Id !== obj?.Id))
    }

    const handleEdit = (data) => {
        if (disabled) return
        setValue(data)
    }

    // Use CSS variables to avoid inline sx/style and keep layout fully in CSS
    // Note: no computed keys to satisfy ESLint "no-useless-computed-key"
    const rootStyleVars = useMemo(
        () => ({
            '--m-transfer-width': typeof width === 'number' ? `${width}px` : String(width),
            '--m-transfer-max-width': typeof maxWidth === 'number' ? `${maxWidth}px` : String(maxWidth),
            '--m-transfer-list-height': typeof height === 'number' ? `${height}px` : String(height),
            '--m-transfer-radius': typeof borderRadius === 'number' ? `${borderRadius}px` : String(borderRadius),

            // Title (same as DynamicForm title)
            '--m-transfer-title-color': 'var(--color-red-dark)',
            '--m-transfer-title-border-color': 'var(--color-red-dark)',

            // Input-like styling (mimic MInputTextField Outlined)
            '--m-transfer-field-border-color': disabled ? 'var(--color-grey)' : resolvedPrimary,
            '--m-transfer-field-border-color-hover': disabled ? 'var(--color-grey)' : resolvedPrimary,
            '--m-transfer-field-border-color-focus': disabled ? 'var(--color-grey)' : resolvedPrimary,
            '--m-transfer-field-shadow-color': disabled ? 'transparent' : resolvedPrimary,

            '--m-transfer-field-bg': 'var(--color-white)',
            '--m-transfer-field-text-color': disabled ? 'var(--color-grey)' : 'var(--color-black)',

            '--m-transfer-danger-color': 'var(--color-red)',
            '--m-transfer-danger-hover-color': 'var(--color-dark-red)',

            '--m-transfer-bg': 'var(--color-white)',
            '--m-transfer-secondary-color': disabled ? 'var(--color-grey)' : resolvedSecondary,
        }),
        [width, maxWidth, height, borderRadius, disabled, resolvedPrimary, resolvedSecondary]
    )

    return (
        <Grid className="m-transfer-root" style={rootStyleVars}>
            <Card className="m-transfer-card" data-testid="card-component">
                <CardHeader
                    className="m-transfer-cardHeader"
                    title={<span className="m-transfer-title">{header}</span>}
                    action={
                        !disabled && (
                            <MButton
                                aria-label="edit"
                                tooltip={t('Edit')}
                                large
                                multiple
                                icon={<img src="./icons/svgsDaNet/edit.svg" alt="Edit" className="m-transfer-icon" />}
                                onSubmitValue={handleEdit}
                                entity={entity}
                                FILTER={'Inactive eq false'}
                                value={items}
                                valueIdField={valueIdField}
                            />
                        )
                    }
                />

                <CardContent className="m-transfer-cardContent">
                    <List className="m-transfer-list" dense component="div">
                        {items.length > 0 ? (
                            items.map((obj, index) => {
                                const text = obj?.[field] ?? t('NoData')
                                return (
                                    <ListItem
                                        key={(obj?.[field] || 'empty') + index}
                                        className={`m-transfer-listItem ${disabled ? 'is-disabled' : ''}`}
                                        disableGutters
                                        secondaryAction={
                                            !disabled && (
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleDelete(obj)}
                                                    className="m-transfer-deleteBtn"
                                                >
                                                    <img
                                                        src="./icons/svgsDaNet/trash-2.svg"
                                                        alt="Delete"
                                                        className="m-transfer-trashIcon"
                                                    />
                                                </IconButton>
                                            )
                                        }
                                    >
                                        <div className="m-transfer-itemRow">
                                            {/* Input-like shell (no label) */}
                                            <div className="m-transfer-inputShell" tabIndex={disabled ? -1 : 0}>
                                                <span className="m-transfer-inputValue">{String(text)}</span>
                                            </div>
                                        </div>
                                    </ListItem>
                                )
                            })
                        ) : (
                            <div className="m-transfer-empty">
                                <Typography variant="body2" color="textSecondary">
                                    {t('NoData')}
                                </Typography>
                            </div>
                        )}
                    </List>
                </CardContent>
            </Card>
        </Grid>
    )
}

MTransferList.propTypes = {
    header: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
    setValue: PropTypes.func.isRequired,
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    entity: PropTypes.string,
    field: PropTypes.string.isRequired,
    valueIdField: PropTypes.string,
    disabled: PropTypes.bool,

    // Layout props
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}
